import {
  getCookieValue,
  setCookieValue,
} from "src/utils/cookies/cookie-getter";
import translations from "./translations";
import { TranslationKey } from "./types";
import {
  HH_LOCALE_COOKIE_NAME,
  ALL_SUPPORTED_LOCALES,
  SUPPORTED_LOCALE,
  defaultLocale,
  languageToLocaleMap,
} from "./constants";
import {
  GLOBAL_LOCALES,
  ROUTES_FOR_GLOBAL_LOCALE_TRANSLATION,
} from "src/utils/global/constants";
import { matchRoute } from "src/utils/global/globalTranslationUtils";
import { localeMappings } from "src/i18n/constants";

class I18nService {
  translationMap: Map<string, string> = new Map();

  constructor() {
    // we will first pull the locale from the cookie, then the path prefix if its not there
    const userLocale =
      this.getLocaleFromCookie() ?? this.getLocaleFromPathPrefix();
    this.setTranslations(userLocale);
  }

  setTranslations(userLocale: SUPPORTED_LOCALE): void {
    // provide fallback
    const defaultTranslation = translations[defaultLocale];

    let allTranslations: Record<string, string>;

    // Determine the current path if available.
    let currentPath = "";
    if (typeof window !== "undefined") {
      currentPath = window.location.pathname;
    }

    const pathPrefix = this.getPathPrefixFromPathName();

    // remove extra slashes if present and then check for locale translation
    currentPath = currentPath.replace(/\/+/g, "/");
    currentPath = currentPath.replace(pathPrefix, "/");

    const matchesGlobalRoute = ROUTES_FOR_GLOBAL_LOCALE_TRANSLATION.some(
      pattern => matchRoute(pattern, currentPath),
    );

    if (
      GLOBAL_LOCALES.includes(userLocale) &&
      !matchesGlobalRoute &&
      typeof window !== "undefined"
    ) {
      allTranslations = defaultTranslation;
    } else {
      allTranslations = {
        ...defaultTranslation,
        ...translations[userLocale],
      };
    }

    Object.keys(allTranslations).forEach(key => {
      this.translationMap.set(key, allTranslations[key]);
    });
  }

  getTranslation(
    translationKey: TranslationKey,
    values?: Record<string, string>,
  ): string {
    let translation = this.translationMap.get(translationKey) || "";
    for (const [key, value] of Object.entries(values || {})) {
      translation = translation.replace(`{${key}}`, value);
    }
    return translation;
  }

  setLocaleToCookie(locale: SUPPORTED_LOCALE): void {
    const ONE_MONTH_IN_SECONDS = 60 * 60 * 24 * 30; // 30 days
    setCookieValue(HH_LOCALE_COOKIE_NAME, locale, ONE_MONTH_IN_SECONDS);
  }

  getLocaleFromCookie(): SUPPORTED_LOCALE | undefined {
    return Object.values(SUPPORTED_LOCALE).find(
      key => key === getCookieValue(HH_LOCALE_COOKIE_NAME),
    );
  }

  verifyUserIsInCorrectLocale(): void {
    let cookieLocale = this.getLocaleFromCookie();
    if (!cookieLocale) {
      // we haven't set the locale cookie
      const preferredLocale = this.getUsersPreferredLocaleFromBrowserSettings();
      this.setLocaleToCookie(preferredLocale);
      cookieLocale = preferredLocale;
    }

    this.redirectUserToPreferredLocale(cookieLocale);
  }

  redirectUserToPreferredLocale(locale: SUPPORTED_LOCALE): void {
    if (typeof window === "undefined" || !window) {
      return;
    }
    const currentUrl = window.location.href.toString();
    const url = new URL(currentUrl);
    const host = url.host;

    if (host.startsWith("localhost") || host.startsWith("127.0.0.1")) {
      return;
    }

    const existingPathPrefix = this.getPathPrefixFromPathName();
    const localeMapping = localeMappings.find(
      mapping => locale === mapping.locale,
    );

    const redirectPathPrefix =
      localeMapping?.redirectPathPrefix ?? localeMapping?.pathPrefix;

    if (!redirectPathPrefix || existingPathPrefix === redirectPathPrefix) {
      return;
    }

    const pathname = url.pathname.replace(
      existingPathPrefix,
      redirectPathPrefix ?? "/",
    );

    // Use URL API to safely construct the redirect URL and prevent DOM-based XSS
    const redirectUrl = new URL(`https://${host}`);
    redirectUrl.pathname = pathname;
    // URL API safely handles search params and hash encoding
    redirectUrl.search = window.location.search;
    redirectUrl.hash = window.location.hash;

    window.location.href = redirectUrl.href;
  }

  getUsersLocale(): SUPPORTED_LOCALE {
    const userLocale =
      this.getLocaleFromCookie() ?? this.getLocaleFromPathPrefix();
    return userLocale ?? defaultLocale;
  }

  getUsersPreferredLocaleFromBrowserSettings(): SUPPORTED_LOCALE {
    if (typeof window === "undefined" || !window) {
      return defaultLocale;
    }

    // the languages are served in order of priority
    // if the first language is `fr`, you need to return a locale that matches that
    const languages = window.navigator?.languages ?? [];
    const preferredLocale = languages.reduce<SUPPORTED_LOCALE | undefined>(
      (acc, curr) => {
        if (acc != null) {
          return acc;
        }

        // Check if the preferred language is shorthand ("fr", "en")
        if (languageToLocaleMap[curr]) {
          return languageToLocaleMap[curr];
        }

        // Check if the preferred language is the full locale ("fr-CA", "en-US")
        const matchedLocale = ALL_SUPPORTED_LOCALES.find(
          locale => locale === curr,
        );
        if (matchedLocale) {
          return matchedLocale;
        }
        return acc;
      },
      undefined,
    );

    return preferredLocale ?? defaultLocale;
  }

  getPathPrefixFromPathName(): string {
    if (typeof window === "undefined" || !window) {
      // return en-US path prefix for default locale
      return "";
    }

    try {
      const pathName = window?.location?.pathname;
      const countryPath = pathName?.split("/")[1];
      const localePath = pathName?.split("/")[2];

      const pathPrefix = `/${countryPath}/${localePath}/`;

      const mappedPathPrefix = localeMappings.find(
        mapping => pathPrefix === mapping.pathPrefix,
      )?.pathPrefix;

      return mappedPathPrefix ?? "/";
    } catch (err) {
      console.log(err);
      return "";
    }
  }

  getLocaleFromPathPrefix(): SUPPORTED_LOCALE {
    if (typeof window === "undefined" || !window) {
      const gatsbyLocaleValue = ALL_SUPPORTED_LOCALES.find(
        locale => locale === process.env.GATSBY_CONTENTFUL_LOCALE,
      );
      return gatsbyLocaleValue ?? defaultLocale;
    }
    try {
      const pathPrefix = this.getPathPrefixFromPathName();

      const locale = localeMappings.find(
        mapping => pathPrefix === mapping.pathPrefix,
      )?.locale;

      return (locale as SUPPORTED_LOCALE) ?? defaultLocale;
    } catch (err) {
      console.log(err);
      return defaultLocale;
    }
  }
}

const i18nService = new I18nService();
export default i18nService;
